import {useEffect, useState} from 'react';
import {Form, Input, Select} from 'antd';
import {useMutation} from '@apollo/client';
import {UPDATE_APP_CONFIGURATION} from 'graphql/queries/mobileSettings';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {walletCurrencies} from 'utils/mock';
import FadeEffect from 'components/animation/fadeEffect';
import deleteIcon from 'images/delete.svg';
import plusIcon from 'images/plus-circle.png';
import styles from './subscriptionPrices.module.scss';
import {DABA_PRO_PLAN} from 'pages/adminUser/userDetails/tabs/Interactions/UserInteractions';

const {Option} = Select;

export const SubscriptionPrices = ({data, refetch}) => {
  const [form] = Form.useForm();
  const [openFinishModal, setOpenFinishModal] = useState(false);

  // Watch both form lists to track used currencies
  const watchedProPlanPrices = Form.useWatch('proPlanPrices', form) || [];
  const watchedAcademyPlanPrices =
    Form.useWatch('academyPlanPrices', form) || [];

  const [updateAppConfig, {loading: loadingUpdate}] = useMutation(
    UPDATE_APP_CONFIGURATION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  // Initialize form with fetched data
  useEffect(() => {
    if (data?.dabaSubscription) {
      const subscriptionData = data.dabaSubscription;
      form.setFieldsValue({
        proPlanPrices: subscriptionData.proPlanPrices || [],
        academyPlanPrices: subscriptionData.academyPlanPrices || [],
      });
    }
  }, [data, form]);

  // Get available currencies (excluding already used ones)
  const getAvailableCurrencies = (
    currentFieldName,
    currentIndex,
    currentCurrency,
  ) => {
    const allUsed = {};

    // Add currencies from proPlanPrices (except current field)
    watchedProPlanPrices.forEach((item, index) => {
      if (
        item?.currency &&
        !(currentFieldName === 'proPlanPrices' && index === currentIndex)
      ) {
        allUsed[item.currency] = true;
      }
    });

    // Add currencies from academyPlanPrices (except current field)
    watchedAcademyPlanPrices.forEach((item, index) => {
      if (
        item?.currency &&
        !(currentFieldName === 'academyPlanPrices' && index === currentIndex)
      ) {
        allUsed[item.currency] = true;
      }
    });

    let available = walletCurrencies.filter(currency => !allUsed[currency.key]);

    // Include current currency if it exists and is not already in available list
    if (currentCurrency) {
      const currentCurrencyObj = walletCurrencies.find(
        c => c.key === currentCurrency,
      );
      if (
        currentCurrencyObj &&
        !available.find(c => c.key === currentCurrency)
      ) {
        available.push(currentCurrencyObj);
      }
    }
    // Sort currencies: XOF first, then USD, then EUR, then XAF, then rest alphabetically
    const priorityOrder = ['XOF', 'USD', 'EUR', 'XAF'];
    return [...available].sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a.key);
      const bIndex = priorityOrder.indexOf(b.key);

      // If both are in priority order, sort by their index
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only a is in priority order, it comes first
      if (aIndex !== -1) return -1;
      // If only b is in priority order, it comes first
      if (bIndex !== -1) return 1;
      // If neither is in priority order, sort alphabetically
      return a.key.localeCompare(b.key);
    });
  };

  const handleSubmit = () => {
    const formValues = form.getFieldsValue();
    updateAppConfig({
      variables: {
        data: {
          dabaSubscription: {
            proPlanPrices: (formValues.proPlanPrices || []).map(item => ({
              amount: Number(item.amount || 0),
              currency: item.currency,
              subscriptionType: item.subscriptionType || '',
            })),
            academyPlanPrices: (formValues.academyPlanPrices || []).map(
              item => ({
                amount: Number(item.amount || 0),
                currency: item.currency,
              }),
            ),
          },
        },
      },
    })
      .then(({data: {updateAppConfig}}) => {
        if (updateAppConfig.__typename === 'Error') {
          toast.error(updateAppConfig.message);
        } else {
          toast.success('Updated Successfully');
          setOpenFinishModal(false);
          if (refetch) {
            refetch();
          }
        }
      })
      .catch(error => toast.error(error.message || 'Update failed'));
  };

  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <h2>Subscription Prices</h2>
        <p>
          Configure subscription prices for Pro Plan and Academy Plan. Each
          currency can only be used once across both plans.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={() => setOpenFinishModal(true)}>
        {/* Pro Plan Prices Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Pro Plan Prices</h3>
          <Form.List name="proPlanPrices">
            {(fields, {add, remove}) => {
              // Sort fields by currency: XOF first, then USD, EUR, XAF, then rest
              const priorityOrder = ['XOF', 'USD', 'EUR', 'XAF'];
              const sortedFields = [...fields].sort((a, b) => {
                const aCurrency = watchedProPlanPrices[a.name]?.currency || '';
                const bCurrency = watchedProPlanPrices[b.name]?.currency || '';
                const aIndex = priorityOrder.indexOf(aCurrency);
                const bIndex = priorityOrder.indexOf(bCurrency);

                // If both are in priority order, sort by their index
                if (aIndex !== -1 && bIndex !== -1) {
                  return aIndex - bIndex;
                }
                // If only a is in priority order, it comes first
                if (aIndex !== -1) return -1;
                // If only b is in priority order, it comes first
                if (bIndex !== -1) return 1;
                // If neither is in priority order, sort alphabetically
                return aCurrency.localeCompare(bCurrency);
              });

              return (
                <>
                  {sortedFields.map(({key, name: fieldName}) => {
                    const currentCurrency =
                      watchedProPlanPrices[fieldName]?.currency;
                    const availableCurrencies = getAvailableCurrencies(
                      'proPlanPrices',
                      fieldName,
                      currentCurrency,
                    );

                    return (
                      <FadeEffect key={key}>
                        <div className={styles.priceCard}>
                          <div className={styles.priceFields}>
                            <Form.Item
                              label="Currency"
                              name={[fieldName, 'currency']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Currency is required',
                                },
                              ]}>
                              <Select
                                placeholder="Select Currency"
                                onChange={() => {
                                  // Trigger re-render to update available currencies
                                  form.validateFields();
                                }}>
                                {availableCurrencies.map(currency => (
                                  <Option
                                    key={currency.key}
                                    value={currency.key}>
                                    {currency.key}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              label="Amount"
                              name={[fieldName, 'amount']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Amount is required',
                                },
                                {
                                  type: 'number',
                                  min: 0,
                                  message:
                                    'Amount must be greater than or equal to 0',
                                  transform: value => Number(value),
                                },
                              ]}>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                placeholder="0.00"
                              />
                            </Form.Item>
                            <Form.Item
                              label="Subscription Type"
                              name={[fieldName, 'subscriptionType']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Subscription type is required',
                                },
                              ]}>
                              <Select placeholder="Select Type">
                                {DABA_PRO_PLAN.map(item => (
                                  <Option key={item.value} value={item.value}>
                                    {item.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </div>
                          <img
                            src={deleteIcon}
                            alt="Delete"
                            className={styles.deleteIcon}
                            onClick={() => remove(fieldName)}
                          />
                        </div>
                      </FadeEffect>
                    );
                  })}
                  <div
                    onClick={() =>
                      add({
                        currency: '',
                        amount: '',
                        subscriptionType: '',
                      })
                    }
                    className={styles['add-button']}>
                    <img src={plusIcon} alt="Add" />
                    <h1>Add Pro Plan Price</h1>
                  </div>
                </>
              );
            }}
          </Form.List>
        </div>

        {/* Academy Plan Prices Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Academy Plan Prices</h3>
          <Form.List name="academyPlanPrices">
            {(fields, {add, remove}) => {
              // Sort fields by currency: XOF first, then USD, EUR, XAF, then rest
              const priorityOrder = ['XOF', 'USD', 'EUR', 'XAF'];
              const sortedFields = [...fields].sort((a, b) => {
                const aCurrency =
                  watchedAcademyPlanPrices[a.name]?.currency || '';
                const bCurrency =
                  watchedAcademyPlanPrices[b.name]?.currency || '';
                const aIndex = priorityOrder.indexOf(aCurrency);
                const bIndex = priorityOrder.indexOf(bCurrency);

                // If both are in priority order, sort by their index
                if (aIndex !== -1 && bIndex !== -1) {
                  return aIndex - bIndex;
                }
                // If only a is in priority order, it comes first
                if (aIndex !== -1) return -1;
                // If only b is in priority order, it comes first
                if (bIndex !== -1) return 1;
                // If neither is in priority order, sort alphabetically
                return aCurrency.localeCompare(bCurrency);
              });

              return (
                <>
                  {sortedFields.map(({key, name: fieldName}) => {
                    const currentCurrency =
                      watchedAcademyPlanPrices[fieldName]?.currency;
                    const availableCurrencies = getAvailableCurrencies(
                      'academyPlanPrices',
                      fieldName,
                      currentCurrency,
                    );

                    return (
                      <FadeEffect key={key}>
                        <div className={styles.priceCard}>
                          <div className={styles.priceFields}>
                            <Form.Item
                              label="Currency"
                              name={[fieldName, 'currency']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Currency is required',
                                },
                              ]}>
                              <Select
                                placeholder="Select Currency"
                                onChange={() => {
                                  // Trigger re-render to update available currencies
                                  form.validateFields();
                                }}>
                                {availableCurrencies.map(currency => (
                                  <Option
                                    key={currency.key}
                                    value={currency.key}>
                                    {currency.key}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              label="Amount"
                              name={[fieldName, 'amount']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Amount is required',
                                },
                                {
                                  type: 'number',
                                  min: 0,
                                  message:
                                    'Amount must be greater than or equal to 0',
                                  transform: value => Number(value),
                                },
                              ]}>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                placeholder="0.00"
                              />
                            </Form.Item>
                          </div>
                          <img
                            src={deleteIcon}
                            alt="Delete"
                            className={styles.deleteIcon}
                            onClick={() => remove(fieldName)}
                          />
                        </div>
                      </FadeEffect>
                    );
                  })}
                  <div
                    onClick={() =>
                      add({
                        currency: '',
                        amount: '',
                      })
                    }
                    className={styles['add-button']}>
                    <img src={plusIcon} alt="Add" />
                    <h1>Add Academy Plan Price</h1>
                  </div>
                </>
              );
            }}
          </Form.List>
        </div>

        <div className={styles.submitContainer}>
          <SubmitButton label="Update" />
        </div>
      </Form>

      <ConfirmModal
        openModal={openFinishModal}
        setOpenModal={() => setOpenFinishModal(false)}
        confirmText="Are you sure you want to update the subscription prices?"
        title="Confirm Update"
        loading={loadingUpdate}
        handleOperation={handleSubmit}
      />
    </div>
  );
};
